import { LineContent } from './LineContent';
import { type Block, HEADING_CONFIG, HEADING_REGEX, hasBlockSyntax, parseBlocks } from './parseBlocks';

const renderBlock = (block: Block, blockIndex: number, blocks: Block[], prevWasInline: boolean) => {
    if (block.type === 'hr') {
        return (
            <hr
                key={`hr-${block.lineIndex}`}
                className="mx-2 mt-6 mb-2 border-t border-gray-300"
            />
        );
    }

    if (block.type === 'heading') {
        const headingMatch = block.line.match(HEADING_REGEX)!;
        const level = headingMatch[1].length;
        const headingContent = headingMatch[2];
        const { Component, variant } = HEADING_CONFIG[level];
        const prev = [...blocks.slice(0, blockIndex)].reverse().find(b => b.type !== 'blank');
        const needsTopPadding = prev && prev.type !== 'heading';

        return (
            <Component
                key={`heading-${block.lineIndex}`}
                variant={variant}
                className={needsTopPadding ? 'pt-4' : undefined}
            >
                <LineContent line={headingContent} keyPrefix={`heading-${block.lineIndex}`} />
            </Component>
        );
    }

    if (block.type === 'ul') {
        return (
            <ul key={`ul-${blockIndex}`} className="list-disc pl-6 pb-4">
                {block.items.map(item => (
                    <li key={`li-${item.lineIndex}`}>
                        <LineContent line={item.line} keyPrefix={`li-${item.lineIndex}`} />
                    </li>
                ))}
            </ul>
        );
    }

    if (block.type === 'ol') {
        return (
            <ol key={`ol-${blockIndex}`} start={block.startNumber} className="list-decimal pl-6 pb-4">
                {block.items.map(item => (
                    <li key={`li-${item.lineIndex}`}>
                        <LineContent line={item.line} keyPrefix={`li-${item.lineIndex}`} />
                    </li>
                ))}
            </ol>
        );
    }

    if (block.type === 'blank') {
        // A blank line between two inline text paragraphs must render as a visible
        // paragraph break. Each text block emits its own leading '\n' when the
        // previous block was inline, so emitting one '\n' here yields the full
        // '\n\n' gap (rendered by the whitespace-pre-wrap container). Block-level
        // elements (headings, lists, hr) manage their own spacing, so only emit the
        // newline when the blank sits between two text blocks.
        const nextNonBlank = blocks.slice(blockIndex + 1).find(b => b.type !== 'blank');
        return prevWasInline && nextNonBlank?.type === 'text'
            ? <span key={`blank-${block.lineIndex}`}>{'\n'}</span>
            : null;
    }

    return (
        <span key={`line-${block.lineIndex}`}>
            {prevWasInline ? '\n' : ''}
            <LineContent line={block.line} keyPrefix={`line-${block.lineIndex}`} />
        </span>
    );
};

interface MessageContentProps {
    content: string;
    placeholderMap?: Record<string, string>;
}

export const MessageContent = ({ content, placeholderMap }: MessageContentProps) => {
    let processedContent = content;

    if (placeholderMap) {
        for (const [token, realValue] of Object.entries(placeholderMap)) {
            processedContent = processedContent.split(token).join(realValue);
        }
    }

    const lines = processedContent.split('\n');

    if (!hasBlockSyntax(lines)) {
        return <LineContent line={processedContent} keyPrefix="text-0" />;
    }

    const blocks = parseBlocks(lines);
    let prevWasInline = false;

    return blocks.map((block, blockIndex) => {
        const element = renderBlock(block, blockIndex, blocks, prevWasInline);
        if (block.type === 'text') {
            prevWasInline = true;
        }
        else if (block.type !== 'blank') {
            prevWasInline = false;
        }
        // blank blocks preserve prevWasInline so a following text block still emits
        // its leading '\n', completing the paragraph break.
        return element;
    });
};
